import * as React from 'react';
import injectSheet, { JSSClassesProps } from 'react-jss';
import { range } from 'lodash';

import { UiImageWithLoader, UiImageWithLoaderPendingMode } from 'sletat-uikit2/dist/js/UiImageWithLoader';
import { ContentSlider as _ContentSlider, ContentSliderProps, SlideSourceParameters } from 'sletat-ui-components/lib/ContentSlider';

import { NoPhotoPlaceholder } from './NoPhotoPlaceholder';
import { getHotelPhotoUrl, getPlaceHolderImage } from '../../../utils/url';

import { ComponentJSSClasses, contentSliderStyles } from './styles';


const ContentSlider = injectSheet<ContentSliderProps>(contentSliderStyles)(_ContentSlider);


export interface PhotoGalleryProps {
    isMobileView: boolean;
    hotelId: number | null;
    resortId: number | null;
    hotelName: string | null;
    hotelImagesCount: number | null;
    hotelBeachSandType: string | null;
}

export interface StyledPhotoGalleryProps extends JSSClassesProps<ComponentJSSClasses>, PhotoGalleryProps { }


export class PhotoGallery extends React.Component<StyledPhotoGalleryProps, {}> {

    private hotelPhotosContainerRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        // SLT-3112 обновляем компонент, чтобы hotelPhotosContainerRef != null,
        // это нужно для расчёта ширины и высоты лоадера в UiImageWithLoader
        this.forceUpdate();
    }

    render() {
        const { classes } = this.props;

        return (
            <div
                ref={this.hotelPhotosContainerRef}
                className={classes.container}
            >
                {this.renderContent()}
            </div>
        );
    }

    renderContent(): JSX.Element | null {
        if (!this.props.hotelId || !this.props.hotelImagesCount) {
            return this.renderPlaceholder();
        }

        if (this.isHotelHasOnlyOnePhoto) {
            return this.renderOnePhoto();
        }

        return this.renderGallery();
    }

    renderGallery(): JSX.Element | null {
        if (!this.props.hotelImagesCount) {
            return null;
        }

        return (
            <div>
                {this.props.hotelBeachSandType &&
                    this.renderHotelLabels()
                }
                <ContentSlider
                    slidesCount={this.props.hotelImagesCount}
                    widthOffsetRatio={0.9}
                    sizeAspectRatio={0.5}
                    shouldShowArrows={!this.props.isMobileView}
                    slideSource={params => this.carouselImagesSource(params)}
                    counterSource={activeSlideId => this.renderGalleryCounter(activeSlideId)}
                />
            </div>
        );
    }

    renderGalleryCounter(activeIdx: number): JSX.Element {
        return (
            <span>
                {activeIdx + 1} / {this.props.hotelImagesCount}
            </span>
        );
    }

    renderHotelLabels(): JSX.Element {
        const { classes } = this.props;

        return (
            <div className={classes.hotelLabels}>
                {this.props.hotelBeachSandType}
            </div>
        );
    }

    renderOnePhoto(): JSX.Element | null {
        if (!this.props.hotelId || !this.hotelPhotosContainerRef.current) {
            return null;
        }

        const { classes } = this.props;

        const imageAspectRatio = 1.8;
        const paddingWidth = this.props.isMobileView ? 24 : 12;

        const loaderWidth = this.hotelPhotosContainerRef.current.clientWidth - paddingWidth;
        const loaderHeight = Math.round(loaderWidth / imageAspectRatio);
        const width = 1256; // максимальная ширина (1280px) - отступы (2 * 12px)
        const height = Math.round(width / imageAspectRatio);

        const imageSrc = getHotelPhotoUrl({
            hotelId: this.props.hotelId,
            index: 0,
            width: width,
            height: height
        });

        return (
            <div className={classes.onePhotoWrap}>
                <UiImageWithLoader
                    src={imageSrc}
                    pendingMode={UiImageWithLoaderPendingMode.once}
                    imgProps={{ alt: this.props.hotelName || '' }}
                    loaderStyles={{
                        width: `${loaderWidth}px`,
                        height: `${loaderHeight}px`
                    }}
                />
            </div>
        );
    }

    renderPlaceholder(): JSX.Element | null {
        if (!this.props.resortId || !this.hotelPhotosContainerRef.current) {
            return null;
        }

        const { classes } = this.props;

        const imageAspectRatio = 1.8;
        const paddingWidth = this.props.isMobileView ? 24 : 12;

        const loaderWidth = this.hotelPhotosContainerRef.current.clientWidth - paddingWidth;
        const loaderHeight = Math.round(loaderWidth / imageAspectRatio);
        const width = 1024;
        const height = 576;

        const src = getPlaceHolderImage(width, height);

        return (
            <div className={classes.onePhotoWrap}>
                <UiImageWithLoader
                    src={src}
                    errorStatus={<NoPhotoPlaceholder />}
                    pendingMode={UiImageWithLoaderPendingMode.once}
                    loaderStyles={{
                        width: `${loaderWidth}px`,
                        height: `${loaderHeight}px`
                    }}
                />
            </div>
        );
    }

    private get isHotelHasOnlyOnePhoto(): boolean {
        return this.props.hotelImagesCount === 1;
    }

    // источник для фоток карусели
    private get carouselImagesSource(): (params: SlideSourceParameters) => JSX.Element | null {
        return ({ idx, activeIdx, width }) => {

            if (range(Math.max(0, idx - 2), idx + 2).indexOf(activeIdx) < 0) {
                return null;
            }

            const imageAspectRatio = 1.8;
            const imgWidth = 1024;
            const imgHeight = Math.round(imgWidth / imageAspectRatio);
            const src = getHotelPhotoUrl({
                hotelId: this.props.hotelId!,
                index: idx,
                width: imgWidth,
                height: imgHeight
            });
            const loaderHeight = width / imageAspectRatio;

            const incrementIndex = (i: number): number => i + 1;

            return (
                <UiImageWithLoader
                    src={src}
                    pendingMode={UiImageWithLoaderPendingMode.once}
                    imgProps={{ alt: `${this.props.hotelName} - фото ${incrementIndex(idx)}` }}
                    loaderStyles={{
                        width: `${width}px`,
                        height: `${loaderHeight}px`
                    }}
                />
            );
        };
    }
}
